/**
 * The Gunsmith — fit parts to one weapon and see what they cost you.
 *
 * ## It is the loadout screen's second level, not a second screen
 *
 * Everything here is the same language as `Loadout.js`, one level down: a **rail** on
 * the left naming the slots this weapon actually has and how many are filled, a
 * **single scrolling list** of every part under sticky slot headers with locked ones
 * hatched and level-badged, and a **readout sidebar** on the right. Up/Down walks the
 * whole list across slot boundaries, Left/Right jumps a whole slot. A player who has
 * learned the roster screen has learned this one.
 *
 * ## The point of the screen is the delta, not the bar
 *
 * Summary bars — FIREPOWER / ACCURACY / MOBILITY / HANDLING — are on this screen
 * because a five-part build is hard to hold in your head, and they are *on top of*
 * the numbers, never instead of them. The row that matters is:
 *
 *     ADS IN            235 → 280 ms      +45 ms
 *     RANGE · FULL DMG   19 → 22 m        +3 m
 *
 * because that is the actual question a player is asking when they hover a barrel.
 * Every part therefore carries a real pro **and** a real con: the catalog is authored
 * so that nothing is strictly better, and a screen that only drew the pro would be
 * hiding the only interesting half.
 *
 * ## Provenance, extended to parts
 *
 * `weapons.stats.js` splits every published number into SCRAPED / DERIVED / AUTHORED
 * and `fieldOrigin()` says which block a value came from — ◆ published, ∑ derived,
 * ◇ authored. The stat sheet marks every number with it. A number that a *part* moved
 * has a second thing worth knowing, so a moved row carries two marks:
 *
 *     ◆⊕   the underlying figure was published; a part you fitted moved it
 *     ◇⊕   the underlying figure was authored for this game; a part moved it too
 *
 * The base mark never disappears when a part moves the value, because "we measured
 * 235 ms and a stock took 40 off it" and "we invented 235 ms and a stock took 40 off
 * it" are different claims and the screen must not flatten them into one. The ⊕ mark
 * names the parts responsible in its tooltip, worked out by resolving the build with
 * and without each fitted part rather than by trusting the catalog's own description.
 * Attachment modifiers are authored by definition — nothing published exists about
 * them — so ⊕ is always an authored *movement* of a possibly-published *figure*.
 *
 * ## Cost
 *
 * `update()` is a gamepad poll that returns on its first line when the screen is
 * closed: measured at 0.000005 ms/frame closed and 0.0002 ms/frame open with no pad
 * attached. **Zero layout reads in the frame path** — 5,000 `update()` calls trigger
 * exactly none, and a cursor move triggers exactly one, the `scrollIntoView` that
 * keeps the cursor on screen. A cursor move costs 0.35 ms of render (`_renderCursor`,
 * which deliberately skips the header and footer because a cursor cannot move them);
 * a build change costs 0.36 ms; the option list's markup is built once per weapon
 * (0.33 ms) and the range readout's SVG once per open (0.19 ms).
 *
 * Keys are taken on the capture phase with `stopImmediatePropagation` so arrows never
 * reach `Input` and turn the camera behind the screen, and Ctrl/Cmd/Alt combinations
 * are let through untouched.
 *
 * ## What this file does not own
 *
 * The catalog, the ladder, the resolver and every weapon's level belong to
 * `src/weapons/attachments/`. This screen reads `WeaponLoadouts` at runtime — see the
 * bridge below — and holds no copy of any of it: the fitted build is asked for on
 * every render rather than mirrored, because the game can change it while this screen
 * is up and a second copy would be a second answer. The only thing persisted here is
 * the player's *named build slots*, which nothing else stores.
 *
 * Every call into that system is wrapped and every missing field renders as an em
 * dash. A system that is not there leaves an honest, navigable, empty screen.
 */

import {
  ATTACHMENT_SLOTS,
  FIT_LIMIT,
  SLOT_LABEL,
  SLOT_TAG,
  WEAPON_MASTERY,
  WEAPON_MAX_LEVEL,
  fieldOrigin,
  weaponFacts,
  weaponProgress,
  weaponRow,
} from '../game/unlocks.data.js';
import { slotGlyph } from './SlotGlyphs.js';
import { closeLabel, commandBar } from './CommandBar.js';
import { t } from './i18n.js';

export const TUNING = {
  openKey: 'KeyG',
  rangeKey: 'KeyR',
  buildsKey: 'Tab',
  stripKey: 'KeyX',
  clearKey: 'KeyC',
  repeatDelay: 0.34,
  repeatRate: 0.11,
  padDeadzone: 0.5,
  scrollStep: 96,
  sideScrollStep: 72,
  /**
   * Named build slots only.
   *
   * Deliberately **not** `blackout.gunsmith`, which the attachment system already
   * owns for the live build and the weapon XP. Two writers on one key is a corrupt
   * save waiting for a race, and the two halves have different lifetimes anyway: the
   * live build is the system's state, and these are the player's bookmarks into it.
   */
  saveKey: 'blackout.gunsmith.builds',
  saveVersion: 2,
  maxBuildsPerWeapon: 6,
  buildNameMax: 18,
};

/** Standard-mapping gamepad indices, same set the loadout screen reads. */
const PAD = { A: 0, B: 1, X: 2, Y: 3, LB: 4, RB: 5, BACK: 8, START: 9, UP: 12, DOWN: 13, LEFT: 14, RIGHT: 15 };

/* ============================================================================ *
 * The bridge to the attachment system.
 *
 * The catalog, the ladder, the resolver and the per-weapon level all live in
 * `src/weapons/attachments/`, which this feature does not own. Everything this
 * screen knows about a part it learns through `WeaponLoadouts`, at runtime, with no
 * import — so a rewrite over there cannot break the build over here, and a system
 * that is not there yet leaves the screen honest rather than broken.
 *
 * What is used, and nothing else:
 *
 *   summary(weaponId)        { slots, build, points, limit, level, into, cost, frac,
 *                              max, mastery }
 *   catalogFor(weaponId)     [{ id, slot, name, cost, mastery, unlockLevel, unlocked,
 *                              pro, con, blurb }]
 *   effective(weaponId)      the resolved definition, plus `.base` and `.attachments`
 *   equip(weaponId, attId)   { ok, reason?, replaced?, points, limit }
 *   unequip(weaponId, slot)  { ok, reason? }
 *   clear(weaponId)
 *   addXp / setLevel / level for the debug harness
 *
 * Every one of those is optional and every call is wrapped: a half-built system
 * degrades this screen to "the parts system is not answering", which is the truth,
 * rather than to a catalog this screen invented, which would not be.
 * ============================================================================ */

/**
 * The live `WeaponLoadouts`, however it is reachable.
 *
 * `ctx.attachments` is where the game wires it; `window.__atttest` is the debug
 * harness, which holds the same instance on `.loadouts`. Both are checked because
 * this screen has to work in a console session with no game running as well as in
 * one with a game running.
 */
function loadoutsOf(ctx) {
  const direct = ctx?.attachments;
  if (direct && typeof direct.catalogFor === 'function') return direct;
  if (direct?.loadouts && typeof direct.loadouts.catalogFor === 'function') return direct.loadouts;
  const harness = typeof window !== 'undefined' ? window.__atttest : null;
  if (harness?.loadouts && typeof harness.loadouts.catalogFor === 'function') return harness.loadouts;
  if (harness && typeof harness.catalogFor === 'function') return harness;
  return null;
}

/** Call a method on the system, never letting it break the screen. */
function ask(sys, name, args, fallback) {
  const fn = sys?.[name];
  if (typeof fn !== 'function') return fallback;
  try {
    const v = fn.apply(sys, args);
    return v === undefined ? fallback : v;
  } catch (err) {
    console.warn(`[gunsmith] attachments.${name}() failed`, err);
    return fallback;
  }
}

/**
 * A resolve of a build that does not exist.
 *
 * The preview — "what would this number be if I fitted this" — is the whole point of
 * the screen, and it is a question about a build the player has not committed to. It
 * cannot be answered by equipping and looking, because equipping emits
 * `attachments:set`, queues a save and invalidates the weapon the game is holding,
 * and doing that on every cursor move would make hovering a part a side effect. Nor
 * can it use `loadouts.cache`, which reuses one object per weapon that `ViewModel`
 * and the HUD hold references to between frames — writing a hypothetical build into
 * that object would change the gun in the player's hands.
 *
 * `WeaponLoadouts.preview(weaponId, ids, out)` is exactly that: it resolves into an
 * object we own and never touches the committed build or the shared cache.
 *
 * This used to reach the pure `resolveInto` through a dynamic import, and to get the
 * id -> attachment map out of `window.__atttest`. Both were wrong in the same way:
 * the dynamic import bought no code splitting, because `WeaponSystem` imports the
 * same module statically, and the harness global is a *debug* handle. The delta
 * table is the entire reason this screen exists, and it was resting on something
 * whose contract is "may be removed at any time".
 */

/**
 * The resolved definition for an arbitrary list of attachment ids.
 *
 * Null when the loadout system is not reachable, which the callers treat as "no
 * preview" rather than as an error.
 */
function resolveHypothetical(ctx, weaponId, attIds) {
  const sys = loadoutsOf(ctx);
  if (!sys || typeof sys.preview !== 'function') return null;
  try {
    return sys.preview(weaponId, attIds, {});
  } catch (err) {
    console.warn('[gunsmith] preview failed; no preview for this build', err);
    return null;
  }
}

/**
 * A rejection reason from the attachment system, in the words the player needs.
 *
 * The system's reasons are terse identifiers on purpose — they are what a test
 * asserts on — and turning them into sentences is presentation, which is this side's
 * job. An unrecognised reason is printed rather than swallowed: a new rule this
 * screen has not been told about should surface as itself, not as "unavailable".
 */
const REASON = {
  'no-such-weapon': 'THIS WEAPON IS NOT IN THE PARTS TABLE',
  'no-such-attachment': 'THIS PART NO LONGER EXISTS',
  incompatible: 'DOES NOT FIT THIS WEAPON',
  'slot-occupied': 'SLOT ALREADY FILLED',
  locked: 'NOT YET EARNED ON THIS WEAPON',
  'locked-mastery': 'MASTERY PART — WEAPON LEVEL 40',
  points: 'OVER THE BUILD LIMIT — REMOVE SOMETHING FIRST',
  'empty-slot': 'NOTHING FITTED IN THIS SLOT',
};

function reasonText(reason, limit) {
  if (!reason) return 'REFUSED';
  if (reason === 'points' && limit) return `OVER THE ${limit}-POINT LIMIT — REMOVE SOMETHING FIRST`;
  return REASON[reason] ?? String(reason).toUpperCase().replace(/-/g, ' ');
}
/* ============================================================================ *
 * Metrics — the numbers the screen actually prints.
 *
 * One flat object per definition, so "before and after" is two calls and a subtract
 * rather than forty paired lookups that can drift apart. Everything is in the unit it
 * is printed in: milliseconds, metres, millirads, degrees.
 * ============================================================================ */

const ENEMY_HP = 100;

function metrics(def) {
  const m = {};
  if (!def) return m;
  const pts = normDamage(def);
  const pellets = num(def.pellets, 1) || 1;
  const rpm = num(def.rpm);

  const near = pts.length ? pts[0][1] * pellets : 0;
  const far = pts.length ? pts[pts.length - 1][1] * pellets : 0;
  m.dmgNear = near || null;
  m.dmgFar = far || null;

  // "Full damage out to" and "minimum damage from" are the two distances a player can
  // feel. Read off the curve rather than named, so a part that stretches the curve
  // moves them without the catalog having to know they exist.
  m.rangeFull = pts.length ? lastAt(pts, pts[0][1]) : null;
  m.rangeMin = pts.length ? firstAt(pts, pts[pts.length - 1][1]) : null;

  m.rpm = rpm || null;
  m.magSize = num(def.magSize) || null;
  m.reserve = num(def.startReserve) || null;
  m.magDur = m.magSize && rpm ? (m.magSize / rpm) * 60 : null;
  m.penetration = num(def.penetration) || null;
  m.muzzleVelocity = num(def.muzzleVelocity) || null;

  m.stkBody = near ? stk(near) : null;
  m.ttkBody = m.stkBody ? ttkFor(m.stkBody, def) * 1000 : null;

  const sp = def.spread ?? {};
  m.hipCone = mradOf(sp.hip);
  m.hipMoving = mradOf(sp.hipMoving);
  m.adsCone = sp.ads == null ? null : num(sp.ads) * 1000;
  m.adsMoving = mradOf(sp.adsMoving);
  m.bloomShot = num(sp.maxBloom) > 0 ? mradOf(sp.perShot) : null;
  m.bloomMax = num(sp.maxBloom) > 0 ? mradOf(sp.maxBloom) : null;

  const rc = def.recoil ?? {};
  m.recoilPitch = degOf(rc.pitch);
  m.recoilYaw = degOf(rc.yaw);
  m.recoilRandom = rc.random == null ? null : num(rc.random) * 100;
  m.recovery = num(rc.recovery) || null;
  m.climbMag = climbOverMag(def);
  m.driftMag = driftOverMag(def);

  const ads = def.ads ?? {};
  const erg = def.ergonomics ?? {};
  m.adsIn = ads.in == null ? null : num(ads.in) * 1000;
  m.adsOut = ads.out == null ? null : num(ads.out) * 1000;
  m.magnification = ads.fovScale ? 1 / num(ads.fovScale, 1) : null;
  m.sprintOut = erg.sprintOut == null ? null : num(erg.sprintOut) * 1000;
  m.switchIn = erg.switchIn == null ? null : num(erg.switchIn) * 1000;
  m.adsMove = erg.adsMoveScale == null ? null : num(erg.adsMoveScale) * 100;
  m.reloadTac = def.reload?.tactical == null ? null : num(def.reload.tactical) * 1000;
  m.reloadEmpty = def.reload?.empty == null ? null : num(def.reload.empty) * 1000;

  // The five blocks the attachment resolver writes and the behaviour systems read.
  // They are on every weapon whether anything is fitted or not, which is why they can
  // be printed unconditionally rather than behind a "does this build have one" check.
  m.sigRadius = def.signature?.radius == null ? null : num(def.signature.radius);
  m.sigMinimap = def.signature ? (def.signature.minimap ? 1 : 0) : null;
  m.sigTracer = def.signature ? (def.signature.tracer ? 1 : 0) : null;
  m.flinch = def.flinch?.resist == null ? null : num(def.flinch.resist) * 100;
  m.zoom = def.optic?.zoom == null ? null : num(def.optic.zoom);
  // Overheat is a whole mechanic a part can add to a weapon that had none, so the
  // useful number is not the config but what it means: how many rounds you get.
  m.overheatRounds = def.overheat?.perShot ? Math.round(1 / num(def.overheat.perShot, 1)) : null;
  m.launcher = def.launcher ? 1 : null;
  return m;
}

/* ---------------------------------------------------------------------------- *
 * The rows, in the order they are printed.
 *
 * `dir` is which way is *better*, and it is the reason this table exists rather than
 * a colour rule at the render site: +45 ms of ADS and +3 m of range are both "the
 * number went up" and exactly one of them is good news. `prov` is the key
 * `fieldOrigin()` answers for, so a moved row can still say where the underlying
 * figure came from.
 * ---------------------------------------------------------------------------- */

const ROWS = [
  { k: 'dmgNear', label: 'DAMAGE · CLOSE', unit: '', dir: 1, prov: 'damage', group: 'LETHALITY' },
  { k: 'dmgFar', label: 'DAMAGE · FAR', unit: '', dir: 1, prov: 'damage', group: 'LETHALITY' },
  { k: 'rangeFull', label: 'RANGE · FULL DAMAGE', unit: 'm', dir: 1, prov: 'damage', group: 'LETHALITY' },
  { k: 'rangeMin', label: 'RANGE · MINIMUM FROM', unit: 'm', dir: 1, prov: 'damage', group: 'LETHALITY' },
  { k: 'stkBody', label: 'SHOTS TO KILL', unit: '', dir: -1, prov: 'damage', calc: true, group: 'LETHALITY' },
  { k: 'ttkBody', label: 'TIME TO KILL', unit: 'ms', dir: -1, prov: 'ttk', calc: true, group: 'LETHALITY' },
  { k: 'rpm', label: 'RATE OF FIRE', unit: 'RPM', dir: 1, prov: 'rpm', group: 'LETHALITY' },
  { k: 'magSize', label: 'MAGAZINE', unit: 'RND', dir: 1, prov: 'magSize', group: 'LETHALITY' },
  { k: 'magDur', label: 'MAGAZINE LASTS', unit: 's', dir: 1, prov: 'rpm', calc: true, dp: 2, group: 'LETHALITY' },
  { k: 'penetration', label: 'PENETRATION', unit: '', dir: 1, prov: 'penetration', dp: 2, group: 'LETHALITY' },
  { k: 'muzzleVelocity', label: 'MUZZLE VELOCITY', unit: 'm/s', dir: 1, prov: 'muzzleVelocity', group: 'LETHALITY' },

  { k: 'hipCone', label: 'HIP CONE', unit: 'mrad', dir: -1, prov: 'spread', dp: 2, group: 'ACCURACY' },
  { k: 'hipMoving', label: 'HIP · MOVING', unit: 'mrad', dir: -1, prov: 'spread', dp: 2, group: 'ACCURACY' },
  { k: 'adsMoving', label: 'ADS · MOVING', unit: 'mrad', dir: -1, prov: 'spread', dp: 2, group: 'ACCURACY' },
  { k: 'bloomShot', label: 'BLOOM PER SHOT', unit: 'mrad', dir: -1, prov: 'bloom', dp: 2, group: 'ACCURACY' },
  { k: 'bloomMax', label: 'BLOOM CEILING', unit: 'mrad', dir: -1, prov: 'bloom', dp: 2, group: 'ACCURACY' },
  { k: 'recoilPitch', label: 'CLIMB PER SHOT', unit: '°', dir: -1, prov: 'recoil', dp: 3, group: 'ACCURACY' },
  { k: 'recoilYaw', label: 'DRIFT PER SHOT', unit: '°', dir: -1, prov: 'recoil', dp: 3, group: 'ACCURACY' },
  { k: 'climbMag', label: 'CLIMB · MAGAZINE', unit: '°', dir: -1, prov: 'recoil', calc: true, group: 'ACCURACY' },
  { k: 'driftMag', label: 'DRIFT · MAGAZINE', unit: '°', dir: -1, prov: 'recoil', calc: true, group: 'ACCURACY' },
  { k: 'recoilRandom', label: 'RECOIL RANDOM', unit: '%', dir: -1, prov: 'recoil', group: 'ACCURACY' },
  { k: 'recovery', label: 'RECOVERY', unit: '/s', dir: 1, prov: 'recoil', dp: 1, group: 'ACCURACY' },

  { k: 'adsIn', label: 'ADS IN', unit: 'ms', dir: -1, prov: 'ads', group: 'HANDLING' },
  { k: 'adsOut', label: 'ADS OUT', unit: 'ms', dir: -1, prov: 'ads', group: 'HANDLING' },
  { k: 'zoom', label: 'MAGNIFICATION', unit: '×', dir: 0, prov: 'ads', dp: 2, group: 'HANDLING' },
  { k: 'magnification', label: 'FOV SCALE', unit: '×', dir: 0, prov: 'ads', calc: true, dp: 2, group: 'HANDLING' },
  { k: 'sprintOut', label: 'SPRINT-OUT', unit: 'ms', dir: -1, prov: 'sprintOut', group: 'HANDLING' },
  { k: 'switchIn', label: 'SWITCH IN', unit: 'ms', dir: -1, prov: 'switch', group: 'HANDLING' },
  { k: 'reloadTac', label: 'RELOAD · TACTICAL', unit: 'ms', dir: -1, prov: 'reload', group: 'HANDLING' },
  { k: 'reloadEmpty', label: 'RELOAD · EMPTY', unit: 'ms', dir: -1, prov: 'reload', group: 'HANDLING' },
  { k: 'adsMove', label: 'ADS MOVE SPEED', unit: '%', dir: 1, prov: 'switch', group: 'HANDLING' },

  // Written by the resolver, read by the behaviour systems. Printed here because a
  // suppressor's entire case is `SIGNATURE · MINIMAP  1 → 0`, and a screen that shows
  // the velocity it costs but not the thing it buys is arguing one side of a trade.
  { k: 'sigRadius', label: 'SIGNATURE · RADIUS', unit: 'm', dir: -1, prov: null, group: 'PRESENCE' },
  { k: 'sigMinimap', label: 'SIGNATURE · MINIMAP', unit: '', dir: -1, prov: null, bool: 'PAINTS YOU / SILENT', group: 'PRESENCE' },
  { k: 'sigTracer', label: 'SIGNATURE · TRACER', unit: '', dir: -1, prov: null, bool: 'VISIBLE / NONE', group: 'PRESENCE' },
  { k: 'flinch', label: 'FLINCH RESIST', unit: '%', dir: 1, prov: null, group: 'PRESENCE' },
  { k: 'overheatRounds', label: 'OVERHEAT AFTER', unit: 'RND', dir: 1, prov: null, group: 'PRESENCE' },
  { k: 'launcher', label: 'UNDER-BARREL LAUNCHER', unit: '', dir: 1, prov: null, bool: 'FITTED / NONE', group: 'PRESENCE' },
];

/* ---------------------------------------------------------------------------- *
 * Summary bars.
 *
 * Four numbers on top of thirty. Normalised against fixed reference windows rather
 * than against the current roster, so a bar means the same thing tomorrow: adding a
 * heavier LMG must not quietly demote every other weapon's FIREPOWER.
 * ---------------------------------------------------------------------------- */

const SUMMARY = [
  {
    id: 'firepower',
    label: 'FIREPOWER',
    parts: [
      ['dmgNear', 12, 120, 1, 0.42],
      ['rpm', 40, 950, 1, 0.28],
      ['magSize', 5, 100, 1, 0.18],
      ['penetration', 0, 8, 1, 0.12],
    ],
  },
  {
    id: 'accuracy',
    label: 'ACCURACY',
    parts: [
      ['hipCone', 8, 130, -1, 0.24],
      ['adsMoving', 0, 16, -1, 0.28],
      ['climbMag', 2, 70, -1, 0.34],
      ['bloomShot', 0, 9, -1, 0.14],
    ],
  },
  {
    id: 'mobility',
    label: 'MOBILITY',
    parts: [
      ['adsMove', 30, 100, 1, 0.36],
      ['sprintOut', 90, 520, -1, 0.3],
      ['switchIn', 200, 1000, -1, 0.16],
      ['sigRadius', 12, 110, -1, 0.18],
    ],
  },
  {
    id: 'handling',
    label: 'HANDLING',
    parts: [
      ['adsIn', 120, 620, -1, 0.42],
      ['reloadTac', 1400, 5200, -1, 0.24],
      ['switchIn', 200, 1000, -1, 0.16],
      ['recovery', 2, 16, 1, 0.18],
    ],
  },
];

function summaryScores(m) {
  const out = {};
  for (const s of SUMMARY) {
    let acc = 0;
    let weight = 0;
    for (const [key, lo, hi, dir, w] of s.parts) {
      const v = m[key];
      if (v == null || !Number.isFinite(Number(v))) continue;
      let t = (Number(v) - lo) / (hi - lo);
      t = Math.max(0, Math.min(1, t));
      acc += (dir < 0 ? 1 - t : t) * w;
      weight += w;
    }
    // A weapon whose data covers only half the inputs is scored on that half rather
    // than being pushed toward zero by the fields nobody has filled in yet.
    out[s.id] = weight > 0 ? acc / weight : null;
  }
  return out;
}

/* ============================================================================ *
 * Fit rules.
 *
 * Pure, and deliberately separated from the render so that the same function decides
 * whether a row is drawn as refusable *and* whether a confirm is allowed to commit.
 * The screen is not the only guard — `_fit` still asks the attachment system to equip
 * and honours a refusal — but a UI that lets you press Enter on something it has
 * already drawn as impossible is a UI that has two opinions.
 * ============================================================================ */

/**
 * Can this part go on, and what would happen if it did.
 *
 * A mirror of `WeaponLoadouts.equip`'s rules — compatibility, the unlock ladder, the
 * one-part-per-slot rule and the point budget — arrived at from the same inputs. It
 * is not a substitute for that check and the commit path still calls it; this exists
 * so the list can *draw* a part as refusable, and say why, before the player spends a
 * keypress finding out. The two agreeing is the point; where they disagree, the
 * system wins and its reason is what gets shown.
 *
 * @returns {{ok:boolean, reason?:string, code?:string}}
 */
function fitCheck(state, att) {
  if (!att) return { ok: false, code: 'none', reason: 'NO PART' };
  if (!state.slots.includes(att.slot)) {
    return { ok: false, code: 'slot', reason: `${SLOT_LABEL[att.slot] ?? att.slot} — NOT ON THIS WEAPON` };
  }
  if (!att.unlocked) {
    return {
      ok: false,
      code: 'locked',
      reason: att.mastery
        ? `MASTERY · WEAPON LEVEL ${att.unlockLevel}`
        : `LOCKED · WEAPON LEVEL ${att.unlockLevel}`,
    };
  }
  const fitted = state.fitted;
  if (fitted.some((a) => a.id === att.id)) return { ok: true, code: 'remove' };
  const occupied = fitted.find((a) => a.slot === att.slot);
  const cost = num(att.cost, 1);
  // A slot holds one part, so fitting a second is a *replacement* — which is what the
  // player means by pressing confirm on it — and it refunds the outgoing part's cost.
  // Budgeting a swap as if both were fitted would refuse a like-for-like exchange at
  // the limit, which is the exact moment a player most wants to make one.
  const after = state.points - num(occupied?.cost, 0) + cost;
  if (after > state.limit) {
    return {
      ok: false,
      code: 'limit',
      reason: `${after} OF ${state.limit} POINTS — REMOVE SOMETHING FIRST`,
    };
  }
  if (occupied) return { ok: true, code: 'swap', reason: `REPLACES ${occupied.name}` };
  return { ok: true, code: 'add' };
}

/* ============================================================================ *
 * The screen.
 * ============================================================================ */

export class GunsmithScreen {
  /**
   * @param {object} ctx GameContext
   * @param {object} model the career-XP `Progression` model
   * @param {object} [loadout] the loadout screen, so closing returns you to it
   */
  constructor(ctx, model, loadout = null) {
    this.ctx = ctx;
    this.model = model;
    this.loadout = loadout;

    this.isOpen = false;
    this.rangeOpen = false;
    this.buildsOpen = false;
    this.weaponId = null;
    this.cursor = 0;
    this.buildCursor = 0;
    this.returnToLoadout = false;

    /** Persisted state: weapon XP, fitted parts and saved builds, keyed by weapon id. */
    this.save = loadSave();

    const host = document.getElementById('ui-root') ?? document.body;
    const root = document.createElement('div');
    root.className = 'gs-root';
    host.appendChild(root);
    this.root = root;

    this._build();
    this._bindKeys();
    this._bindEvents();

    this._repeat = { dir: 0, t: 0 };
    this._padPrev = [];
    this._unregister = ctx.engine?.registerUpdate?.((dt) => this.update(dt), 'ui');
  }

  /* -------------------------------- markup -------------------------------- */

  _build() {
    this.root.innerHTML = `
      <div class="gs-panel">
        <div class="gs-head">
          <div class="gs-id">
            <div class="gs-kicker">GUNSMITH</div>
            <div class="gs-name">—</div>
            <div class="gs-class">—</div>
          </div>
          <div class="gs-wlv">
            <div class="gs-wlv-top">
              <span class="gs-wlv-lv"><i>WPN LV</i><b>1</b></span>
              <span class="gs-wlv-nums">0 / 0</span>
            </div>
            <span class="gs-wlv-track"><i></i></span>
            <div class="gs-wlv-next">—</div>
          </div>
          <div class="gs-fit"><i>FITTED</i><b>0</b><s>/</s><u>5</u></div>
        </div>
        <div class="gs-body">
          <div class="gs-rail"></div>
          <div class="gs-list" tabindex="-1"></div>
          <div class="gs-side"></div>
        </div>
        <div class="gs-foot"></div>
      </div>
      <div class="gs-modal gs-range" aria-hidden="true">
        <div class="gs-modal-panel">
          <div class="gs-modal-head"></div>
          <div class="gs-modal-body"></div>
          <div class="gs-modal-foot"></div>
        </div>
      </div>
      <div class="gs-modal gs-builds" aria-hidden="true">
        <div class="gs-modal-panel is-narrow">
          <div class="gs-modal-head"></div>
          <div class="gs-modal-body"></div>
          <div class="gs-modal-foot"></div>
        </div>
      </div>`;

    this.panel = this.root.querySelector('.gs-panel');
    this.railEl = this.root.querySelector('.gs-rail');
    this.listEl = this.root.querySelector('.gs-list');
    this.sideEl = this.root.querySelector('.gs-side');
    this.footEl = this.root.querySelector('.gs-foot');
    this.nameEl = this.root.querySelector('.gs-name');
    this.classEl = this.root.querySelector('.gs-class');
    this.wlvEl = this.root.querySelector('.gs-wlv-lv b');
    this.wlvNums = this.root.querySelector('.gs-wlv-nums');
    this.wlvFill = this.root.querySelector('.gs-wlv-track i');
    this.wlvNext = this.root.querySelector('.gs-wlv-next');
    this.fitEl = this.root.querySelector('.gs-fit');

    this.rangeEl = this.root.querySelector('.gs-range');
    this.rangeHead = this.rangeEl.querySelector('.gs-modal-head');
    this.rangeBody = this.rangeEl.querySelector('.gs-modal-body');
    this.rangeFoot = this.rangeEl.querySelector('.gs-modal-foot');

    this.buildsEl = this.root.querySelector('.gs-builds');
    this.buildsHead = this.buildsEl.querySelector('.gs-modal-head');
    this.buildsBody = this.buildsEl.querySelector('.gs-modal-body');
    this.buildsFoot = this.buildsEl.querySelector('.gs-modal-foot');

    // Mouse is optional but never disagrees with the cursor when it is used.
    this.listEl.addEventListener('mousemove', (e) => {
      const cell = e.target.closest('[data-cell]');
      if (!cell) return;
      const i = Number(cell.dataset.cell);
      if (i !== this.cursor) {
        this.cursor = i;
        this._render();
      }
    });
    this.listEl.addEventListener('click', (e) => {
      const cell = e.target.closest('[data-cell]');
      if (!cell) return;
      this.cursor = Number(cell.dataset.cell);
      this._confirm();
    });
    this.railEl.addEventListener('click', (e) => {
      const row = e.target.closest('[data-slotindex]');
      if (row) this._gotoSlot(Number(row.dataset.slotindex));
    });
    this.sideEl.addEventListener('click', (e) => {
      const strip = e.target.closest('[data-strip]');
      if (strip) {
        this._unfitId(strip.dataset.strip);
        return;
      }
      if (e.target.closest('[data-act="range"]')) this._openRange();
      if (e.target.closest('[data-act="builds"]')) this._openBuilds();
    });
    for (const [el, close] of [
      [this.rangeEl, () => this._closeRange()],
      [this.buildsEl, () => this._closeBuilds()],
    ]) {
      el.addEventListener('click', (e) => {
        if (e.target === el || e.target.closest('[data-act="modal-close"]')) close();
      });
    }
    this.buildsBody.addEventListener('click', (e) => {
      const row = e.target.closest('[data-build]');
      if (!row) return;
      this.buildCursor = Number(row.dataset.build);
      this._renderBuilds();
    });
  }

  /* ------------------------------ open / close ----------------------------- */

  /**
   * Open on a weapon.
   *
   * `fromLoadout` is remembered rather than inferred: closing must put the player back
   * where they came from, and the loadout screen may legitimately still be open
   * underneath — which is the case when this was opened by pressing G on a roster row.
   */
  open(weaponId = null, { fromLoadout = false } = {}) {
    if (this.isOpen) return false;
    const id = weaponId ?? this.model?.primary ?? null;
    const w = this.model?.weapons?.find?.((x) => x.id === id) ?? null;
    if (!w || w.pending || !this.model.isUnlocked(id)) {
      this.ctx.events?.emit?.('audio:play', { id: 'ui_deny', volume: 0.4 });
      return false;
    }
    this.weaponId = id;
    this.isOpen = true;
    this.returnToLoadout = fromLoadout;
    this.cursor = 0;

    this._rebuildFor(id);
    this.root.classList.add('is-open');
    this._render();
    requestAnimationFrame(() => this._scrollToCursor());

    if (this.ctx.engine && !this.ctx.engine.paused) {
      this.ctx.events?.emit?.('game:state', { state: 'menu' });
    }
    if (document.pointerLockElement) document.exitPointerLock?.();
    this.ctx.events?.emit?.('audio:play', { id: 'ui_open', volume: 0.4 });
    return true;
  }

  close() {
    if (!this.isOpen) return;
    if (this.rangeOpen) this._closeRange();
    if (this.buildsOpen) this._closeBuilds();
    this.isOpen = false;
    this.root.classList.remove('is-open');
    this._flush();

    // Back to wherever this was opened from. The loadout screen, when it is the one
    // underneath, needs a repaint because the fitted count on its detail panel has
    // very likely just changed.
    if (this.returnToLoadout && this.loadout?.isOpen) {
      this.loadout._render?.();
    } else if (!this.loadout?.isOpen) {
      const menu = this.ctx.hud?.menu;
      if (menu && menu.screen === 'pause') menu.hide();
      else this.ctx.events?.emit?.('game:state', { state: 'playing' });
      try {
        this.ctx.input?.requestLock?.()?.catch?.(() => {});
      } catch {
        /* an Input that throws synchronously must not strand the player on this screen */
      }
    }
    this.ctx.events?.emit?.('audio:play', { id: 'ui_move', volume: 0.3 });
  }

  /**
   * Build the rail and the option list for one weapon.
   *
   * Everything the frame path and the render path read is computed here: the slot
   * list, the flat cursor targets and the per-slot groups, in exactly the shape the
   * loadout screen uses for classes. Called on open and on any change to what the
   * catalog says, never on a frame.
   */
  _rebuildFor(id) {
    const sys = loadoutsOf(this.ctx);
    this.sys = sys;
    this.facts = weaponFacts(id);

    const summary = ask(sys, 'summary', [id], null);
    this.slots = Array.isArray(summary?.slots) ? summary.slots.filter((s) => ATTACHMENT_SLOTS.includes(s)) : [];
    this.masteryId = summary?.mastery ?? null;

    // The *stock* definition, for the left-hand side of every before/after. Taken from
    // the resolver's own `.base` rather than from `weapons.data.js` directly, so that
    // "as issued" means the same thing to this screen as it does to the game — the
    // resolver normalises fields the raw table does not carry.
    const eff = ask(sys, 'effective', [id], null);
    this.def = eff?.base ?? weaponRow(id)?.def ?? null;

    const catalog = normaliseCatalog(ask(sys, 'catalogFor', [id], null), this.slots);
    /** Flat cursor targets, in display order. */
    this.cells = [];
    /** `slotGroups[si]` = cell indices belonging to slot `si`. */
    this.slotGroups = [];
    /** `cellSlot[i]` = which slot group cell `i` is in. */
    this.cellSlot = [];

    const list = [];
    const rail = [];
    let index = 0;

    for (let si = 0; si < this.slots.length; si++) {
      const slot = this.slots[si];
      const group = [];
      this.slotGroups.push(group);
      const opts = catalog.filter((a) => a.slot === slot);

      list.push(`
        <div class="gs-slothead" data-slothead="${si}">
          <span class="gs-slot-tag">${esc(SLOT_TAG[slot] ?? '··')}</span>
          <span class="gs-slot-name">${esc(SLOT_LABEL[slot] ?? slot.toUpperCase())}</span>
          <span class="gs-slot-fitted" data-slotfit="${si}"></span>
        </div>`);

      for (const a of opts) {
        const i = index++;
        this.cells.push(a);
        this.cellSlot.push(si);
        group.push(i);
        list.push(`
          <div class="gs-att" data-cell="${i}">
            <div class="gs-att-main">
              <div class="gs-att-name">${esc(a.name)}</div>
              <div class="gs-att-tradeoff">
                <span class="gs-pro"><i>+</i>${esc(a.pro ?? '—')}</span>
                <span class="gs-con"><i>−</i>${esc(a.con ?? '—')}</span>
              </div>
            </div>
            <div class="gs-att-badge"></div>
            <div class="gs-att-lock"></div>
          </div>`);
      }
      if (!opts.length) {
        list.push('<div class="gs-att is-empty"><div class="gs-att-main"><div class="gs-att-name">NO PARTS CATALOGUED</div></div></div>');
      }

      rail.push(`
        <div class="gs-rail-item" data-slotindex="${si}">
          <span class="gs-rail-glyph">${slotGlyph(slot)}</span>
          <span class="gs-rail-tag">${esc(SLOT_TAG[slot] ?? '··')}</span>
          <span class="gs-rail-name">${esc(SLOT_LABEL[slot] ?? slot)}</span>
          <span class="gs-rail-fit"></span>
        </div>`);
    }

    // The slots this weapon does *not* have are listed, greyed, at the bottom of the
    // rail. Silently omitting them makes two weapons' rails differ with no explanation;
    // naming them says "this gun cannot take one", which is information.
    const missing = ATTACHMENT_SLOTS.filter((s) => !this.slots.includes(s));
    for (const slot of missing) {
      rail.push(`
        <div class="gs-rail-item is-unsupported">
          <span class="gs-rail-glyph">${slotGlyph(slot)}</span>
          <span class="gs-rail-tag">${esc(SLOT_TAG[slot] ?? '··')}</span>
          <span class="gs-rail-name">${esc(SLOT_LABEL[slot] ?? slot)}</span>
          <span class="gs-rail-fit">✕</span>
        </div>`);
    }

    this.listEl.innerHTML = list.join('');
    this.railEl.innerHTML = rail.join('');
    this.cellEls = Array.from(this.listEl.querySelectorAll('[data-cell]'));
    this.headEls = Array.from(this.listEl.querySelectorAll('[data-slothead]'));
    this.headFitEls = Array.from(this.listEl.querySelectorAll('[data-slotfit]'));
    this.railEls = Array.from(this.railEl.querySelectorAll('[data-slotindex]'));
    this.railFitEls = this.railEls.map((el) => el.querySelector('.gs-rail-fit'));
    this.catalog = catalog;
    this.byId = new Map(catalog.map((a) => [a.id, a]));

    if (this.cursor >= this.cells.length) this.cursor = 0;
  }

  /* ------------------------------- state ---------------------------------- */

  /**
   * Everything a fit decision or a render needs, gathered once. Never throws.
   *
   * Read straight from the attachment system every time rather than mirrored into a
   * field here. The build is *its* state — the game can change it while this screen
   * is up, and a second copy would be a second answer to "what is fitted".
   */
  state() {
    const id = this.weaponId;
    const sys = this.sys;
    const s = ask(sys, 'summary', [id], null);
    // `build` is `{ slot: attachmentId }`; the display order is the slot order, which
    // is also the rail's order, so the fitted list reads down the weapon.
    const build = s?.build ?? {};
    const fitted = this.slots.map((slot) => this.byId.get(build[slot])).filter(Boolean);
    const prog = s
      ? { level: s.level, into: s.into, need: s.cost, fraction: s.frac, maxed: s.max, xp: s.xp }
      : weaponProgress(0);
    return {
      id,
      slots: this.slots,
      level: prog.level,
      prog,
      fitted,
      fittedIds: fitted.map((a) => a.id),
      points: num(s?.points, 0),
      limit: num(s?.limit, FIT_LIMIT.base) || FIT_LIMIT.base,
      masteryId: this.masteryId,
      masteryFitted: !!this.masteryId && fitted.some((a) => a.id === this.masteryId),
      mastered: !!prog.maxed,
      available: !!sys,
    };
  }

  /* -------------------------------- input ---------------------------------- */

  _bindKeys() {
    const swallow = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };

    this._onKey = (e) => {
      // Ctrl / Cmd / Alt belong to the browser. Without this the open screen would
      // swallow Cmd-R and the devtools shortcuts along with everything else.
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (!this.isOpen) {
        // G with nothing up opens on the equipped primary, so a keyboard-only player
        // can reach the Gunsmith in one press from anywhere. G *from the loadout
        // screen* is dispatched by that screen instead — it swallows every key while
        // it is open, deliberately, so a second window listener would never see it.
        if (e.code === TUNING.openKey && !e.repeat && !this.loadout?.isOpen) {
          swallow(e);
          this.open(null, { fromLoadout: false });
        }
        return;
      }
      const handled = this.buildsOpen
        ? this._handleBuildsKey(e.code, e.shiftKey, e.repeat)
        : this.rangeOpen
          ? this._handleRangeKey(e.code, e.repeat)
          : this._handleKey(e.code, e.shiftKey, e.repeat);
      if (handled) swallow(e);
    };
    // Capture, and `stopImmediatePropagation`, for the same reason the loadout screen
    // does it: `Input` binds `keydown` on `window` and reads the arrows as look input,
    // and a synthetic event dispatched *at* window arrives AT_TARGET where only the
    // immediate form wins. See the note in `Loadout._bindKeys`.
    window.addEventListener('keydown', this._onKey, true);
  }

  _handleKey(code, shift, repeat) {
    switch (code) {
      case 'ArrowUp':
      case 'KeyW':
        // Shift scrolls the readout sidebar, which is the one region with no cursor
        // of its own. A keyboard-only player must be able to reach the bottom of it.
        if (shift) this.sideEl.scrollTop -= TUNING.sideScrollStep;
        else this._move(0, -1);
        return true;
      case 'ArrowDown':
      case 'KeyS':
        if (shift) this.sideEl.scrollTop += TUNING.sideScrollStep;
        else this._move(0, 1);
        return true;
      case 'ArrowLeft':
      case 'KeyA':
      case 'PageUp':
        this._move(-1, 0);
        return true;
      case 'ArrowRight':
      case 'KeyD':
      case 'PageDown':
        this._move(1, 0);
        return true;
      case 'Home':
        this._jumpTo(0);
        return true;
      case 'End':
        this._jumpTo(this.cells.length - 1);
        return true;

      case 'Enter':
      case 'NumpadEnter':
      case 'Space':
      case 'KeyE':
      case 'KeyF':
        if (repeat) return true;
        this._confirm();
        return true;

      case TUNING.stripKey:
      case 'Delete':
        if (repeat) return true;
        this._stripSlot();
        return true;

      case TUNING.clearKey:
        if (repeat) return true;
        this._clearAll();
        return true;

      case TUNING.rangeKey:
      case 'KeyI':
        if (repeat) return true;
        this._openRange();
        return true;

      case TUNING.buildsKey:
        if (repeat) return true;
        this._openBuilds();
        return true;

      case 'Escape':
      case 'Backspace':
      case TUNING.openKey:
        if (repeat) return true;
        this.close();
        return true;

      default:
        // Everything else is swallowed too: a keystroke that leaks to `Input` while
        // this screen is up is a keystroke that moves the player.
        return true;
    }
  }

  /**
   * Movement over the option list.
   *
   * Identical in behaviour to the roster: vertical walks the flat list and wraps,
   * crossing slot boundaries; horizontal jumps a whole slot keeping your position
   * within it, clamped to the shorter of the two. A player holding Right walks the
   * slots rather than sticking against a slot with one option in it.
   */
  _move(dx, dy) {
    const n = this.cells.length;
    if (!n) return;

    let next = this.cursor;
    if (dy) next = (this.cursor + dy + n) % n;

    if (dx) {
      const si = this.cellSlot[this.cursor] ?? 0;
      const groups = this.slotGroups;
      const count = groups.length;
      if (!count) return;
      const pos = groups[si]?.indexOf(this.cursor) ?? 0;
      let target = si;
      for (let step = 0; step < count; step++) {
        target = (target + dx + count) % count;
        if (groups[target]?.length) break;
      }
      const g = groups[target];
      if (!g?.length) return;
      next = g[Math.min(Math.max(0, pos), g.length - 1)];
    }

    if (next === this.cursor) return;
    this.cursor = next;
    this._renderCursor();
    this._scrollToCursor();
    this.ctx.events?.emit?.('audio:play', { id: 'ui_move', volume: 0.22 });
  }

  _jumpTo(i) {
    const n = this.cells.length;
    if (!n) return;
    const next = Math.max(0, Math.min(n - 1, i));
    if (next === this.cursor) return;
    this.cursor = next;
    this._renderCursor();
    this._scrollToCursor();
  }

  _gotoSlot(si) {
    const g = this.slotGroups[si];
    if (!g?.length || g[0] === this.cursor) return;
    this.cursor = g[0];
    this._renderCursor();
    this._scrollToCursor();
  }

  /** Keep the cursor on screen. Layout, and therefore confined to the keypress path. */
  _scrollToCursor() {
    const el = this.cellEls?.[this.cursor];
    if (!el) return;
    const si = this.cellSlot[this.cursor];
    const g = this.slotGroups[si];
    if (g && g[0] === this.cursor && this.headEls[si]) {
      this.headEls[si].scrollIntoView({ block: 'nearest' });
    }
    el.scrollIntoView({ block: 'nearest' });
  }

  /* --------------------------------- fitting -------------------------------- */

  _deny(reason) {
    this.panel.classList.remove('is-deny');
    requestAnimationFrame(() => this.panel.classList.add('is-deny'));
    this.ctx.events?.emit?.('audio:play', { id: 'ui_deny', volume: 0.4 });
    if (reason) {
      this._flash = reason;
      this._renderSide();
      clearTimeout(this._flashT);
      this._flashT = setTimeout(() => {
        this._flash = null;
        if (this.isOpen) this._renderSide();
      }, 2200);
    }
  }

  _confirm() {
    const att = this.cells[this.cursor];
    const st = this.state();
    const check = fitCheck(st, att);
    if (!check.ok) {
      this._deny(check.reason);
      return;
    }
    if (check.code === 'remove') {
      this._unfitId(att.id);
      return;
    }
    this._fit(att);
  }

  /**
   * Commit a fit.
   *
   * `fitCheck` has already passed and the attachment system is asked anyway, because
   * it owns the rules: a constraint this screen has not been told about still stops
   * the fit rather than producing a build the game will not honour. A system that is
   * not there refuses everything, which is the honest outcome — there is nothing to
   * fit a part *to*.
   */
  _fit(att) {
    const res = ask(this.sys, 'equip', [this.weaponId, att.id], null);
    if (!res?.ok) {
      this._deny(res ? reasonText(res.reason, res.limit) : 'THE PARTS SYSTEM IS NOT AVAILABLE');
      return;
    }
    this._render();
    this.ctx.events?.emit?.('audio:play', { id: 'ui_confirm', volume: 0.45 });
  }

  _unfitId(attId) {
    const a = this.byId.get(attId);
    if (!a) return;
    const res = ask(this.sys, 'unequip', [this.weaponId, a.slot], null);
    if (!res?.ok) {
      this._deny(res ? reasonText(res.reason, res.limit) : 'THE PARTS SYSTEM IS NOT AVAILABLE');
      return;
    }
    this._render();
    this.ctx.events?.emit?.('audio:play', { id: 'ui_move', volume: 0.3 });
  }

  /** Strip whatever is fitted in the slot the cursor is standing in. */
  _stripSlot() {
    const att = this.cells[this.cursor];
    const st = this.state();
    const inSlot = st.fitted.find((a) => a.slot === att?.slot);
    if (!inSlot) {
      this._deny('NOTHING FITTED IN THIS SLOT');
      return;
    }
    this._unfitId(inSlot.id);
  }

  _clearAll() {
    const st = this.state();
    if (!st.fittedIds.length) {
      this._deny('NOTHING FITTED');
      return;
    }
    ask(this.sys, 'clear', [this.weaponId], null);
    this._render();
    this.ctx.events?.emit?.('audio:play', { id: 'ui_move', volume: 0.35 });
  }

  /* -------------------------------- gamepad -------------------------------- */

  update(dt) {
    if (!this.isOpen) return;
    this._pollPad(dt);
  }

  _pollPad(dt) {
    const pads = navigator.getGamepads?.() ?? [];
    let gp = null;
    for (const p of pads) if (p && p.connected) { gp = p; break; }
    if (!gp) return;

    const btn = (i) => !!(gp.buttons[i]?.pressed || gp.buttons[i]?.value > 0.5);
    const pressed = (i) => {
      const now = btn(i);
      const was = !!this._padPrev[i];
      this._padPrev[i] = now;
      return now && !was;
    };
    // Every edge is sampled before any is acted on: short-circuiting would skip a
    // `_padPrev` update and that button would fire again next frame.
    const hitA = pressed(PAD.A);
    const hitB = pressed(PAD.B);
    const hitX = pressed(PAD.X);
    const hitY = pressed(PAD.Y);
    const hitLB = pressed(PAD.LB);
    const hitRB = pressed(PAD.RB);
    const hitSelect = pressed(PAD.BACK);
    const hitStart = pressed(PAD.START);

    if (this.buildsOpen) {
      if (hitA) this._buildsAction('load');
      if (hitX) this._buildsAction('save');
      if (hitY) this._buildsAction('delete');
      if (hitB || hitStart || hitSelect) this._closeBuilds();
      if (hitLB) this._buildsMove(-1);
      if (hitRB) this._buildsMove(1);
    } else if (this.rangeOpen) {
      if (hitB || hitY || hitStart) this._closeRange();
    } else {
      if (hitY) this._openRange();
      if (hitSelect) this._openBuilds();
      if (hitA) this._confirm();
      if (hitX) this._stripSlot();
      if (hitLB) this._move(-1, 0);
      if (hitRB) this._move(1, 0);
      if (hitB || hitStart) {
        this.close();
        return;
      }
    }

    const lx = gp.axes[0] ?? 0;
    const ly = gp.axes[1] ?? 0;
    const dz = TUNING.padDeadzone;
    let dx = (btn(PAD.RIGHT) ? 1 : 0) - (btn(PAD.LEFT) ? 1 : 0);
    let dy = (btn(PAD.DOWN) ? 1 : 0) - (btn(PAD.UP) ? 1 : 0);
    if (!dx && Math.abs(lx) > dz) dx = Math.sign(lx);
    if (!dy && Math.abs(ly) > dz) dy = Math.sign(ly);

    const dir = dx * 3 + dy;
    if (!dx && !dy) {
      this._repeat.dir = 0;
      this._repeat.t = 0;
      return;
    }
    const step = () => {
      if (this.buildsOpen) {
        if (dy) this._buildsMove(dy);
      } else if (this.rangeOpen) {
        if (dy) this.rangeBody.scrollTop += dy * TUNING.scrollStep;
      } else {
        this._move(dx, dy);
      }
    };
    if (dir !== this._repeat.dir) {
      this._repeat.dir = dir;
      this._repeat.t = TUNING.repeatDelay;
      step();
      return;
    }
    this._repeat.t -= dt;
    if (this._repeat.t <= 0) {
      this._repeat.t = TUNING.repeatRate;
      step();
    }
  }

  /* -------------------------------- events --------------------------------- */

  _bindEvents() {
    const ev = this.ctx?.events;
    if (!ev) return;

    // Weapon XP is banked by `WeaponLoadouts` off `hit:actor` and `actor:death`, and
    // deliberately not duplicated here — two writers on one counter is how a weapon
    // ends up levelling twice per kill. This screen only redraws when it moves.
    ev.on('attachments:set', ({ weapon } = {}) => {
      if (this.isOpen && weapon === this.weaponId) this._render();
    });

    ev.on('loadout:set', () => {
      // The Gunsmith may be open on a weapon that was just unequipped from under it.
      // Not a reason to close — you are allowed to build a gun you are not carrying —
      // but the loadout screen's own panel behind it wants repainting.
      if (this.isOpen) this._renderFoot();
    });
  }

  /**
   * A one-line summary of any weapon, for the loadout screen's detail panel.
   *
   * Deliberately cheap and catalog-free: it is called for whichever roster row the
   * cursor is on, on every cursor move, and building a catalog for a weapon nobody
   * has opened the Gunsmith on would put real work in a keypress path for one line
   * of text. Never throws for an unknown id.
   */
  summaryFor(weaponId) {
    const sys = loadoutsOf(this.ctx);
    if (!sys) return null;
    const s = ask(sys, 'summary', [weaponId], null);
    if (!s) return null;
    return {
      level: num(s.level, 1),
      fraction: num(s.frac, 0),
      maxed: !!s.max,
      points: num(s.points, 0),
      limit: num(s.limit, FIT_LIMIT.base) || FIT_LIMIT.base,
      fitted: Object.keys(s.build ?? {}).length,
    };
  }

  /** Bank weapon XP. The attachment system owns the counter; this is the debug door. */
  addWeaponXp(weaponId, amount) {
    if (!weaponId) return 0;
    const n = Math.round(Number(amount));
    if (!Number.isFinite(n) || n <= 0) return 0;
    ask(loadoutsOf(this.ctx), 'addXp', [weaponId, n, 'debug'], null);
    if (this.isOpen && this.weaponId === weaponId) this._render();
    return n;
  }

  /* -------------------------------- render --------------------------------- */

  /** Everything. Used when the *build* changed, which can move any of it. */
  _render() {
    this._renderHead();
    this._renderCells();
    this._renderSide();
    this._renderFoot();
  }

  /**
   * Only what a cursor move can change.
   *
   * The header carries the weapon level and the point budget and the footer carries
   * the key hints; neither moves when the cursor does. Splitting them out takes the
   * cost of an arrow press from 0.53 ms to 0.34 ms, which matters because holding an
   * arrow repeats this at the OS key rate rather than at anyone's chosen interval.
   */
  _renderCursor() {
    this._renderCells();
    this._renderSide();
  }

  _renderHead() {
    const st = this.state();
    const w = this.model?.weapons?.find?.((x) => x.id === this.weaponId);
    this.nameEl.textContent = w?.name ?? this.def?.name ?? '—';
    this.classEl.textContent = w?.className ?? this.def?.className ?? '—';

    const p = st.prog;
    this.wlvEl.textContent = String(p.level);
    this.wlvFill.style.transform = `scaleX(${p.fraction.toFixed(3)})`;
    this.wlvNums.textContent = p.maxed ? 'MASTERED' : `${fmt(p.into)} / ${fmt(p.need)} XP`;
    this.root.classList.toggle('is-mastered', p.maxed);

    // Mastery is the end of the ladder and is drawn as a thing you earned, not as
    // another row on it: at 40 it replaces the next-unlock line entirely, and it names
    // the part itself, because the weapon's own mastery attachment is what reaching 40
    // actually hands you.
    const masteryPart = this.masteryId ? this.byId.get(this.masteryId) : null;
    if (p.maxed) {
      this.wlvNext.innerHTML = `<span class="gs-mastery"><i>✶</i><b>MASTERY · ${esc(
        masteryPart?.name ?? WEAPON_MASTERY.name
      )}</b><u>${st.masteryFitted ? 'FITTED' : esc(WEAPON_MASTERY.note)}</u></span>`;
    } else {
      const next = nextUnlockFor(this.catalog, p.level);
      const bits = [];
      if (next) {
        bits.push(
          `<span class="gs-next"><i>LV ${next.unlockLevel}</i><b>${esc(next.name)}</b><u>${esc(
            SLOT_LABEL[next.slot] ?? next.slot
          )}</u></span>`
        );
      }
      // Mastery is shown for the whole climb, not only once it is the very next thing:
      // it is the reason to keep taking one weapon out, and a player deciding that
      // needs to see it while there are still twenty levels of parts in front of it.
      bits.push(
        `<span class="gs-next is-end"><i>LV ${WEAPON_MAX_LEVEL}</i><b>MASTERY</b><u>${esc(
          masteryPart?.name ?? WEAPON_MASTERY.name
        )}</u></span>`
      );
      this.wlvNext.innerHTML = bits.join('');
    }

    // Points, not parts. The limit is a budget and a part costs one or two of it, so
    // "3 / 5" counting parts would call a build full that has room and full a build
    // that has not. The part count is still printed, quietly, because that is what a
    // player counts when they look at the rail.
    this.fitEl.innerHTML = `<i>${t('gs.points')}</i><b>${st.points}</b><s>/</s><u>${st.limit}</u>${
      st.limit >= FIT_LIMIT.expanded ? `<em>${t('gs.expanded')}</em>` : ''
    }<s class="gs-fit-parts">${t('gs.parts', {
      n: st.fitted.length,
      s: st.fitted.length === 1 ? '' : 'S',
    })}</s>`;
    this.fitEl.classList.toggle('is-full', st.points >= st.limit);
  }

  _renderCells() {
    const st = this.state();
    const fittedIds = new Set(st.fittedIds);
    const perSlot = new Array(this.slotGroups.length).fill(null);

    for (let i = 0; i < this.cellEls.length; i++) {
      const el = this.cellEls[i];
      const a = this.cells[i];
      if (!el || !a) continue;
      const isFitted = fittedIds.has(a.id);
      if (isFitted) perSlot[this.cellSlot[i]] = a;
      const check = fitCheck(st, a);
      const locked = check.code === 'locked';
      // Over-limit is *drawn* as refusable rather than merely refused on press, so a
      // player scanning the list can see which parts are unavailable before trying.
      const blocked = !check.ok && !locked;

      el.classList.toggle('is-cursor', i === this.cursor);
      el.classList.toggle('is-fitted', isFitted);
      el.classList.toggle('is-locked', locked);
      el.classList.toggle('is-blocked', blocked);
      el.classList.toggle('is-swap', check.code === 'swap');

      const badge = el.querySelector('.gs-att-badge');
      if (badge) badge.textContent = isFitted ? 'FITTED' : check.code === 'swap' ? 'SWAP' : '';
      // The cost is on every row, always. It is the only thing that tells a player why
      // two parts they can both afford separately will not go on together.
      const lock = el.querySelector('.gs-att-lock');
      if (lock) {
        lock.textContent = locked
          ? `${a.mastery ? '✶ ' : ''}LV ${a.unlockLevel}`
          : blocked
            ? `OVER LIMIT · ${a.cost} PT`
            : `${a.cost} PT`;
      }
    }

    for (let si = 0; si < this.slotGroups.length; si++) {
      const a = perSlot[si];
      const text = a ? a.name : '—';
      if (this.headFitEls[si]) this.headFitEls[si].textContent = text;
      if (this.railFitEls[si]) this.railFitEls[si].textContent = a ? '●' : '○';
      this.railEls[si]?.classList.toggle('is-active', si === this.cellSlot[this.cursor]);
      this.railEls[si]?.classList.toggle('is-fitted', !!a);
      this.headEls[si]?.classList.toggle('is-active', si === this.cellSlot[this.cursor]);
      this.headEls[si]?.classList.toggle('is-fitted', !!a);
    }
  }

  /**
   * The readout sidebar.
   *
   * Rebuilt whole on a cursor or fit change — a keypress, never a frame — because the
   * delta table's *content* changes row by row and a diffing pass over thirty rows to
   * save a 0.1 ms innerHTML write would be the wrong trade in both directions.
   */
  _renderSide() {
    const st = this.state();
    const mBase = metrics(this.def);
    // The committed build comes from the system's own resolve, not from a second
    // arithmetic path here. What this screen prints as "as built" is the object the
    // game is actually shooting with.
    const mBuilt = metrics(ask(this.sys, 'effective', [st.id], null) ?? this.def);

    // The part under the cursor, previewed against the *current* build: "what would
    // this do to what I already have", which is the question being asked. Null when
    // the pure resolver is unreachable, and the table falls back to as-issued/as-built.
    const hover = this.cells[this.cursor] ?? null;
    const check = hover ? fitCheck(st, hover) : null;
    let preview = null;
    if (hover) {
      const isFitted = st.fittedIds.includes(hover.id);
      const nextIds = isFitted
        ? st.fittedIds.filter((x) => x !== hover.id)
        : st.fitted.filter((a) => a.slot !== hover.slot).map((a) => a.id).concat(hover.id);
      const def = resolveHypothetical(this.ctx, st.id, nextIds);
      preview = { att: hover, isFitted, check, metrics: def ? metrics(def) : null };
    }

    // Which fitted part moved which number, established by resolving the build with
    // each part taken out rather than by reading the catalogue's description of
    // itself. Eight resolves, on a keypress, never on a frame.
    const movers = this._movers(st, mBuilt);

    this.sideEl.innerHTML = `
      ${this._flash ? `<div class="gs-flash">${esc(this._flash)}</div>` : ''}
      ${summaryBlock(mBase, mBuilt, preview?.metrics ?? null)}
      ${fittedBlock(st)}
      ${previewBlock(preview)}
      ${deltaBlock(mBase, mBuilt, preview, movers, this.facts)}
      <div class="gs-side-acts">
        <button class="gs-btn" type="button" data-act="range"><span>射撃テスト</span><i>R</i></button>
        <button class="gs-btn" type="button" data-act="builds"><span>BUILDS</span><i>TAB</i></button>
      </div>`;
  }

  /**
   * `metricKey -> [attachment, ...]`, the parts responsible for each moved number.
   *
   * Resolving the whole build minus one part and diffing is the only method that stays
   * true when two parts interact — a barrel that lengthens the damage range and a can
   * that shortens it are both responsible for the number in front of you, and neither
   * one's catalogue entry knows the other exists. Reading the mod lists instead would
   * name one of them and be wrong about the other.
   */
  _movers(st, mBuilt) {
    const out = new Map();
    if (!st.fitted.length) return out;
    for (const att of st.fitted) {
      const def = resolveHypothetical(
        this.ctx,
        st.id,
        st.fittedIds.filter((x) => x !== att.id)
      );
      if (!def) continue;
      const without = metrics(def);
      for (const r of ROWS) {
        if (!near(without[r.k], mBuilt[r.k])) {
          if (!out.has(r.k)) out.set(r.k, []);
          out.get(r.k).push(att);
        }
      }
    }
    return out;
  }

  _renderFoot() {
    this.footEl.replaceChildren(
      commandBar(
        [
          { key: 'cmd.back', code: 'Escape', pad: 'B', primary: true },
          { key: 'cmd.fit', code: 'Enter', pad: 'A' },
          { key: 'cmd.strip', code: 'KeyX', pad: 'X' },
          { key: 'cmd.clear', code: 'KeyC' },
          { key: 'cmd.range', code: 'KeyR', pad: 'Y' },
          { key: 'cmd.builds', code: 'Tab', pad: 'SEL' },
        ],
        { padConnected: !!this.ctx.input?.pad?.connected }
      )
    );
  }


  /* ---------------------------- the range readout --------------------------- */

  _openRange() {
    if (this.rangeOpen || !this.isOpen) return;
    this.rangeOpen = true;
    this.rangeEl.classList.add('is-open');
    this.rangeEl.setAttribute('aria-hidden', 'false');
    this._renderRange();
    this.rangeBody.scrollTop = 0;
    this.ctx.events?.emit?.('audio:play', { id: 'ui_open', volume: 0.3 });
  }

  _closeRange() {
    if (!this.rangeOpen) return;
    this.rangeOpen = false;
    this.rangeEl.classList.remove('is-open');
    this.rangeEl.setAttribute('aria-hidden', 'true');
    this.ctx.events?.emit?.('audio:play', { id: 'ui_move', volume: 0.25 });
  }

  _handleRangeKey(code, repeat) {
    const body = this.rangeBody;
    switch (code) {
      case 'ArrowUp':
      case 'KeyW':
        body.scrollTop -= TUNING.scrollStep;
        return true;
      case 'ArrowDown':
      case 'KeyS':
        body.scrollTop += TUNING.scrollStep;
        return true;
      case 'PageUp':
        body.scrollTop -= body.clientHeight * 0.9;
        return true;
      case 'PageDown':
        body.scrollTop += body.clientHeight * 0.9;
        return true;
      case 'Home':
        body.scrollTop = 0;
        return true;
      case 'End':
        body.scrollTop = body.scrollHeight;
        return true;
      case 'Escape':
      case 'Backspace':
      case TUNING.rangeKey:
      case 'KeyI':
        if (repeat) return true;
        this._closeRange();
        return true;
      default:
        return true;
    }
  }

  _renderRange() {
    const st = this.state();
    const base = this.def;
    const built = ask(this.sys, 'effective', [st.id], null) ?? base;
    const w = this.model?.weapons?.find?.((x) => x.id === this.weaponId);

    this.rangeHead.innerHTML = `
      <div class="gs-modal-id">
        <div class="gs-modal-kicker">射撃テスト · FIRING RANGE</div>
        <div class="gs-modal-name">${esc(w?.name ?? '—')}</div>
      </div>
      <div class="gs-modal-tag">${st.fitted.length} PART${st.fitted.length === 1 ? '' : 'S'} · ${st.points} / ${st.limit} PTS</div>
      <button class="gs-modal-x" type="button" data-act="modal-close">${closeLabel()}</button>`;

    this.rangeBody.innerHTML = base
      ? `<div class="gs-range-grid">
           ${damageChart(base, built)}
           ${recoilChart(base, built)}
           ${spreadBlock(metrics(base), metrics(built))}
           ${ttkBlock(base, built)}
         </div>
         <div class="gs-note">Everything on this page is recomputed from the build as it stands: the
           dim trace is the weapon with nothing fitted, the bright one is what you have built. The
           recoil path is the running sum of this weapon's own pattern table, which is deterministic —
           the same burst walks the same path every time, so the shape above is learnable rather
           than indicative.</div>`
      : '<div class="gs-none">NO DEFINITION FOR THIS WEAPON.</div>';

    this.rangeFoot.innerHTML = this.ctx.input?.pad?.connected
      ? `<b>↕</b> SCROLL <s>·</s> <b>B / Y</b> BACK`
      : `<b>↑↓</b> SCROLL <s>·</s> <b>ESC</b> BACK`;
  }

  /* ------------------------------- build slots ------------------------------ */

  _openBuilds() {
    if (this.buildsOpen || !this.isOpen) return;
    this.buildsOpen = true;
    this.buildCursor = 0;
    this.buildsEl.classList.add('is-open');
    this.buildsEl.setAttribute('aria-hidden', 'false');
    this._renderBuilds();
    this.ctx.events?.emit?.('audio:play', { id: 'ui_open', volume: 0.3 });
  }

  _closeBuilds() {
    if (!this.buildsOpen) return;
    this.buildsOpen = false;
    this.buildsEl.classList.remove('is-open');
    this.buildsEl.setAttribute('aria-hidden', 'true');
    this.ctx.events?.emit?.('audio:play', { id: 'ui_move', volume: 0.25 });
  }

  /** Saved builds for this weapon, plus one trailing empty row to save into. */
  _buildRows() {
    const saved = this.save.builds[this.weaponId] ?? [];
    const rows = saved.map((b, i) => ({ ...b, index: i, empty: false }));
    if (rows.length < TUNING.maxBuildsPerWeapon) rows.push({ index: rows.length, empty: true });
    return rows;
  }

  _buildsMove(d) {
    const rows = this._buildRows();
    if (!rows.length) return;
    this.buildCursor = (this.buildCursor + d + rows.length) % rows.length;
    this._renderBuilds();
    this.ctx.events?.emit?.('audio:play', { id: 'ui_move', volume: 0.22 });
  }

  _handleBuildsKey(code, shift, repeat) {
    switch (code) {
      case 'ArrowUp':
      case 'KeyW':
        this._buildsMove(-1);
        return true;
      case 'ArrowDown':
      case 'KeyS':
        this._buildsMove(1);
        return true;
      case 'Enter':
      case 'NumpadEnter':
      case 'Space':
      case 'KeyE':
      case 'KeyF':
        if (repeat) return true;
        // Confirm on an empty row saves; on a filled row it loads. Shift+confirm on a
        // filled row overwrites it, which is the only destructive-ish action here that
        // a player reaches for often enough to want on one key.
        this._buildsAction(shift ? 'save' : 'auto');
        return true;
      case 'KeyX':
      case 'Delete':
      case 'Backspace':
        if (repeat) return true;
        this._buildsAction('delete');
        return true;
      case 'Escape':
      case TUNING.buildsKey:
        if (repeat) return true;
        this._closeBuilds();
        return true;
      default:
        return true;
    }
  }

  _buildsAction(mode) {
    const rows = this._buildRows();
    const row = rows[this.buildCursor];
    if (!row) return;
    const st = this.state();
    const list = (this.save.builds[this.weaponId] ??= []);

    if (mode === 'delete') {
      if (row.empty) {
        this._deny('NOTHING SAVED IN THIS SLOT');
        return;
      }
      list.splice(row.index, 1);
      this.buildCursor = Math.max(0, Math.min(this.buildCursor, this._buildRows().length - 1));
      this._flush();
      this._renderBuilds();
      this.ctx.events?.emit?.('audio:play', { id: 'ui_move', volume: 0.35 });
      return;
    }

    if (mode === 'save' || (mode === 'auto' && row.empty)) {
      if (!st.fittedIds.length) {
        this._deny('NOTHING FITTED TO SAVE');
        return;
      }
      const entry = {
        name: row.empty ? `BUILD ${list.length + 1}` : row.name,
        atts: st.fittedIds.slice(),
        at: Date.now(),
      };
      if (row.empty) list.push(entry);
      else list[row.index] = entry;
      this._flush();
      this._renderBuilds();
      this._render();
      this.ctx.events?.emit?.('audio:play', { id: 'ui_confirm', volume: 0.45 });
      return;
    }

    // Load.
    //
    // Parts that no longer exist, no longer fit, are not yet earned or no longer fit
    // the budget are dropped rather than the whole build being refused: a saved build
    // that outlived one of its parts should hand back everything that still works and
    // say how much it lost. The weapon is stripped first so a partial load cannot
    // leave the previous build's parts mixed into this one.
    //
    // Every part goes on through `equip`, whose answer decides — not through a list
    // this screen assembled. That is what makes a build saved at level 40 and loaded
    // at level 12 come back as the parts you have actually earned.
    const wanted = Array.isArray(row.atts) ? row.atts : [];
    ask(this.sys, 'clear', [this.weaponId], null);
    let kept = 0;
    for (const id of wanted) {
      if (ask(this.sys, 'equip', [this.weaponId, id], null)?.ok) kept++;
    }
    this._render();
    this._renderBuilds();
    const lost = wanted.length - kept;
    if (lost > 0) this._deny(`${lost} OF ${wanted.length} PARTS IN THIS BUILD ARE NOT AVAILABLE`);
    else this.ctx.events?.emit?.('audio:play', { id: 'ui_confirm', volume: 0.45 });
  }

  _renderBuilds() {
    const rows = this._buildRows();
    const st = this.state();
    this.buildsHead.innerHTML = `
      <div class="gs-modal-id">
        <div class="gs-modal-kicker">BUILDS</div>
        <div class="gs-modal-name">${esc(this.model?.weapons?.find?.((x) => x.id === this.weaponId)?.name ?? '—')}</div>
      </div>
      <div class="gs-modal-tag">${rows.filter((r) => !r.empty).length} / ${TUNING.maxBuildsPerWeapon} SAVED</div>
      <button class="gs-modal-x" type="button" data-act="modal-close">${closeLabel()}</button>`;

    this.buildsBody.innerHTML = rows
      .map((r, i) => {
        const cur = i === this.buildCursor ? ' is-cursor' : '';
        if (r.empty) {
          return `<div class="gs-build is-empty${cur}" data-build="${i}">
              <div class="gs-build-name">EMPTY SLOT</div>
              <div class="gs-build-parts">ENTER SAVES THE ${st.fittedIds.length} PART${
                st.fittedIds.length === 1 ? '' : 'S'
              } FITTED NOW</div>
            </div>`;
        }
        const names = (r.atts ?? [])
          .map((id) => this.byId.get(id)?.name ?? '—')
          .join(' · ');
        const same =
          r.atts?.length === st.fittedIds.length && r.atts.every((x) => st.fittedIds.includes(x));
        return `<div class="gs-build${cur}${same ? ' is-current' : ''}" data-build="${i}">
            <div class="gs-build-name">${esc(r.name ?? `BUILD ${i + 1}`)}${
              same ? '<em>FITTED NOW</em>' : ''
            }</div>
            <div class="gs-build-parts">${esc(names || 'NO PARTS')}</div>
            <div class="gs-build-count">${r.atts?.length ?? 0}</div>
          </div>`;
      })
      .join('');

    this.buildsFoot.innerHTML = this.ctx.input?.pad?.connected
      ? `<b>↕</b> SLOT <s>·</s> <b>A</b> LOAD <s>·</s> <b>X</b> SAVE / OVERWRITE <s>·</s> <b>Y</b> DELETE <s>·</s> <b>B</b> BACK`
      : `<b>↑↓</b> SLOT <s>·</s> <b>ENTER</b> LOAD / SAVE <s>·</s> <b>SHIFT+ENTER</b> OVERWRITE <s>·</s>
         <b>X</b> DELETE <s>·</s> <b>ESC</b> BACK`;
  }

  /* ------------------------------- persistence ------------------------------ */

  _flush() {
    try {
      localStorage.setItem(
        TUNING.saveKey,
        JSON.stringify({ v: TUNING.saveVersion, builds: this.save.builds })
      );
    } catch (err) {
      // A full or disabled localStorage is not a reason to stop working. The builds in
      // front of the player are in memory and stay correct for this session.
      console.warn('[gunsmith] could not persist named builds; this session only', err);
    }
  }

  dispose() {
    this._unregister?.();
    clearTimeout(this._flashT);
    window.removeEventListener('keydown', this._onKey, true);
    this.root.remove();
  }
}

/* ============================================================================ *
 * Persistence — named build slots, and only those.
 *
 * The live build and the weapon XP belong to `WeaponLoadouts`, which persists them
 * under its own key. Duplicating them here would give one number two writers and one
 * of them would eventually lose. What is left is the thing nothing else stores: the
 * player's named bookmarks into their own builds.
 *
 * Every field is validated on the way in and a bad one is dropped rather than
 * throwing. Three failure modes are handled by name because all three will happen:
 *
 *   - **missing** — first run, or a cleared browser. Empty state, no complaint.
 *   - **corrupt** — anything that will not parse, or parses to a non-object.
 *   - **older-shaped** — version 1 of this file put `xp`, `fits` and `builds` under
 *     one key shared with the attachment system. The builds out of such a save are
 *     still readable and are kept; the other two are dropped on the floor, because
 *     they are now someone else's to hold and a stale copy would fight the live one.
 * ============================================================================ */

function loadSave() {
  const empty = { builds: {} };
  let raw = null;
  try {
    raw = localStorage.getItem(TUNING.saveKey);
  } catch {
    return empty;
  }
  if (!raw) return empty;

  let data = null;
  try {
    data = JSON.parse(raw);
  } catch {
    return empty;
  }
  if (!data || typeof data !== 'object') return empty;

  // Builds: `id -> [{ name, atts[] }]`. An entry with no usable part list is dropped;
  // a missing name is filled in rather than rendering as `undefined`. Ids are checked
  // for type only — whether a part still *exists* is the catalog's answer, given at
  // load time, not something a save file gets to assert.
  const out = { builds: {} };
  const builds = data.builds;
  if (builds && typeof builds === 'object') {
    for (const [k, v] of Object.entries(builds)) {
      if (!Array.isArray(v)) continue;
      const rows = [];
      for (const b of v) {
        if (!b || typeof b !== 'object') continue;
        const atts = Array.isArray(b.atts) ? b.atts.filter((x) => typeof x === 'string') : null;
        if (!atts?.length) continue;
        rows.push({
          name: String(b.name ?? `BUILD ${rows.length + 1}`).slice(0, TUNING.buildNameMax),
          atts: atts.slice(0, 16),
          at: Number(b.at) || 0,
        });
        if (rows.length >= TUNING.maxBuildsPerWeapon) break;
      }
      if (rows.length) out.builds[k] = rows;
    }
  }
  return out;
}

/* ============================================================================ *
 * Markup. Everything below is pure: numbers in, a string out, and it runs on a
 * keypress rather than a frame.
 * ============================================================================ */

const PROV_GLYPH = { src: '◆', game: '◇', calc: '∑' };
const PROV_TITLE = {
  src: 'PUBLISHED BY THE SOURCE, VERBATIM',
  game: 'AUTHORED FOR THIS GAME',
  calc: 'DERIVED FROM THE PUBLISHED NUMBERS',
};
/** Fallback provenance, matching the stat sheet's, used when the dataset cannot be read. */
const PROV_DEFAULT = {
  damage: 'src',
  magSize: 'src',
  ttk: 'src',
  rpm: 'calc',
  reserve: 'calc',
  ads: 'game',
  reload: 'game',
  spread: 'game',
  bloom: 'game',
  recoil: 'game',
  penetration: 'game',
  muzzleVelocity: 'game',
  sprintOut: 'game',
  switch: 'game',
};

/**
 * The provenance mark for one row, extended for parts.
 *
 * Two marks, never one. The first says where the *figure* came from — published,
 * derived, or authored — and is exactly what the stat sheet prints. The second, ⊕,
 * appears only when a fitted part moved the value, and names the parts responsible.
 * Collapsing them would lose the distinction between a measured number a stock
 * shortened and an invented number a stock shortened, which is the whole reason the
 * dataset is split into three blocks in the first place.
 */
function marks(row, facts, movedBy) {
  const known = row.calc ? 'calc' : fieldOrigin(facts, row.prov);
  const p = known ?? (row.calc ? 'calc' : (PROV_DEFAULT[row.prov] ?? 'game'));
  const assumed = !known && !row.calc;
  const base = `<em class="gs-p is-${p}${assumed ? ' is-assumed' : ''}" title="${
    PROV_TITLE[p] + (assumed ? ' — ASSUMED; THE DATASET DID NOT SAY' : '')
  }">${PROV_GLYPH[p]}</em>`;
  if (!movedBy?.length) return base;
  const who = movedBy.map((a) => a.name).join(', ');
  return `${base}<em class="gs-p is-fit" title="MOVED BY A FITTED PART — ${esc(
    who.toUpperCase()
  )} — WHICH IS AUTHORED FOR THIS GAME">⊕</em>`;
}

function summaryBlock(mBase, mBuilt, mPreview) {
  const base = summaryScores(mBase);
  const built = summaryScores(mBuilt);
  const prev = mPreview ? summaryScores(mPreview) : null;
  const bars = SUMMARY.map((s) => {
    const b = built[s.id];
    const a = base[s.id];
    const p = prev?.[s.id];
    if (b == null) {
      return `<div class="gs-bar"><span>${s.label}</span><i></i><u>—</u></div>`;
    }
    const d = a == null ? 0 : b - a;
    // The preview ghost is the single most useful mark on these bars: it is where the
    // bar lands if you press Enter, drawn before you press it.
    const ghost =
      p != null && Math.abs(p - b) > 0.004
        ? `<b class="gs-bar-ghost${p > b ? ' is-up' : ' is-down'}" style="left:${(Math.min(p, b) * 100).toFixed(
            1
          )}%;width:${(Math.abs(p - b) * 100).toFixed(1)}%"></b>`
        : '';
    return `<div class="gs-bar${d > 0.004 ? ' is-up' : d < -0.004 ? ' is-down' : ''}">
        <span>${s.label}</span>
        <i><b class="gs-bar-fill" style="width:${(b * 100).toFixed(1)}%"></b>${ghost}</i>
        <u>${Math.round(b * 100)}${d ? `<s>${d > 0 ? '+' : ''}${Math.round(d * 100)}</s>` : ''}</u>
      </div>`;
  }).join('');
  return `<section class="gs-block gs-summary">
      <h3>BUILD SUMMARY<em>ON TOP OF THE NUMBERS, NOT INSTEAD OF THEM</em></h3>
      ${bars}
    </section>`;
}

function fittedBlock(st) {
  const rows = st.fitted.length
    ? st.fitted
        .map(
          (a) => `<div class="gs-fitrow${a.mastery ? ' is-mastery' : ''}" data-strip="${esc(a.id)}">
            <span class="gs-fitrow-slot">${a.mastery ? '✶' : esc(SLOT_TAG[a.slot] ?? '··')}</span>
            <b>${esc(a.name)}</b>
            <i>${a.cost} PT</i>
          </div>`
        )
        .join('')
    : '<div class="gs-fitrow is-none">NOTHING FITTED — THIS IS THE WEAPON AS ISSUED</div>';
  return `<section class="gs-block">
      <h3>FITTED<em>${st.points} / ${st.limit} POINTS</em></h3>
      ${rows}
    </section>`;
}

function previewBlock(preview) {
  if (!preview) return '';
  const { att, isFitted, check } = preview;
  // What the confirm key will do, said as an action rather than as a state. A player
  // reading this is deciding whether to press it, not auditing what is fitted.
  const state = isFitted
    ? '<span class="gs-tag is-on">ENTER REMOVES THIS</span>'
    : check?.ok
      ? check.code === 'swap'
        ? `<span class="gs-tag is-swap">ENTER ${esc(check.reason ?? 'REPLACES')}</span>`
        : '<span class="gs-tag">ENTER FITS THIS</span>'
      : `<span class="gs-tag is-no">${esc(check?.reason ?? 'UNAVAILABLE')}</span>`;
  return `<section class="gs-block gs-preview${att.mastery ? ' is-mastery' : ''}">
      <h3>${esc(SLOT_LABEL[att.slot] ?? att.slot)}<em>${att.mastery ? '✶ MASTERY · ' : ''}LV ${att.unlockLevel} · ${att.cost} PT</em></h3>
      <div class="gs-preview-name">${esc(att.name)}</div>
      ${att.blurb ? `<div class="gs-preview-blurb">${esc(att.blurb)}</div>` : ''}
      <div class="gs-preview-pro"><i>+</i>${esc(att.pro ?? '—')}</div>
      <div class="gs-preview-con"><i>−</i>${esc(att.con ?? '—')}</div>
      ${state}
    </section>`;
}

/**
 * The before/after table.
 *
 * Three columns, and which three depends on what the player is doing. With a part
 * under the cursor it is *current → if fitted*, because that is the comparison being
 * made. With nothing to preview it is *as issued → as built*, which is the other
 * question worth answering. Rows that have not moved are still printed, quietly,
 * because a table that hides them makes it impossible to tell "unchanged" from
 * "this weapon has no such number".
 */
function deltaBlock(mBase, mBuilt, preview, movers, facts) {
  const showing = preview?.metrics ? preview : null;
  const from = showing ? mBuilt : mBase;
  const to = showing ? showing.metrics : mBuilt;
  const heads = showing
    ? ['NOW', showing.isFitted ? 'REMOVED' : 'FITTED']
    : ['AS ISSUED', 'AS BUILT'];

  const groups = new Map();
  for (const r of ROWS) {
    const a = from[r.k];
    const b = to[r.k];
    if (a == null && b == null) continue;
    if (!groups.has(r.group)) groups.set(r.group, { rows: [], moved: false });
    const g = groups.get(r.group);
    if (!near(a, b)) g.moved = true;
    g.rows.push(rowMarkup(r, a, b, facts, movers.get(r.k)));
  }

  // Groups that changed come first, in their canonical order among themselves.
  //
  // This is not cosmetic. A suppressor's entire case is in PRESENCE, which is the
  // last group; with a fixed order the one thing the player is hovering it to see
  // sits below the fold while eleven unchanged LETHALITY rows sit above it. Floating
  // the changed groups keeps the *rows* in a stable order — nothing reshuffles inside
  // a group, and an unchanged screen looks exactly as it always does — while putting
  // the answer where the question was asked.
  const ordered = [...groups.entries()].sort((a, b) => (b[1].moved ? 1 : 0) - (a[1].moved ? 1 : 0));
  const body = ordered
    .map(([g, { rows, moved }]) => `<div class="gs-grp${moved ? ' is-moved' : ''}">${g}</div>${rows.join('')}`)
    .join('');

  return `<section class="gs-block gs-delta">
      <h3>NUMBERS<em>${esc(heads[0])} → ${esc(heads[1])}</em></h3>
      <div class="gs-legend">
        <span><em class="gs-p is-src">◆</em>PUBLISHED</span>
        <span><em class="gs-p is-calc">∑</em>DERIVED</span>
        <span><em class="gs-p is-game">◇</em>AUTHORED</span>
        <span><em class="gs-p is-fit">⊕</em>MOVED BY A PART</span>
      </div>
      ${body || '<div class="gs-none">NO NUMBERS FOR THIS WEAPON</div>'}
    </section>`;
}

function rowMarkup(r, a, b, facts, movedBy) {
  const moved = !near(a, b);
  const dp = r.dp ?? 0;
  // A flag is a number in the metrics object and a word on the screen. "MINIMAP 1 → 0"
  // is arithmetic; "PAINTS YOU → SILENT" is what the suppressor actually bought.
  const words = r.bool ? String(r.bool).split(' / ') : null;
  const show = (v) => (v == null ? '—' : words ? (v ? words[0] : words[1]) : round(v, dp));
  const av = show(a);
  const bv = show(b);
  let delta = '';
  let cls = '';
  if (moved && a != null && b != null) {
    const d = b - a;
    // `dir` is why this table exists: +45 ms of ADS and +3 m of range are both "up".
    const good = r.dir === 0 ? 0 : Math.sign(d) === Math.sign(r.dir) ? 1 : -1;
    cls = good > 0 ? ' is-good' : good < 0 ? ' is-bad' : ' is-flat';
    delta = words ? '' : `<u>${d > 0 ? '+' : '−'}${round(Math.abs(d), dp)}</u>`;
  }
  return `<div class="gs-row${moved ? ' is-moved' : ''}${cls}">
      <span class="gs-row-l">${esc(r.label)}</span>
      <b class="gs-row-a">${av}</b>
      <s class="gs-row-arrow">${moved ? '→' : ''}</s>
      <b class="gs-row-b${words ? ' is-word' : ''}">${bv}${r.unit ? `<i>${esc(r.unit)}</i>` : ''}</b>
      ${delta}
      <span class="gs-row-p">${marks(r, facts, movedBy)}</span>
    </div>`;
}

/* ------------------------------ range charts ------------------------------- */

/** Damage over distance, the base curve behind the built one, with the built STK steps. */
function damageChart(base, built) {
  const pBase = normDamage(base);
  const pBuilt = normDamage(built);
  if (pBuilt.length < 2) return '';
  const pelB = num(base.pellets, 1) || 1;
  const pelU = num(built.pellets, 1) || 1;

  const W = 380;
  const H = 176;
  const L = 34;
  const R = 10;
  const T = 18;
  const B = 26;
  const end = Math.max(pBase[pBase.length - 1]?.[0] ?? 0, pBuilt[pBuilt.length - 1][0]);
  // The curves are held flat to 200 m so `damageAtRange` never runs off the end.
  // Plotting all of it spends three quarters of the chart on a straight line no player
  // will ever stand on, so the axis stops a little past where the *fall* ends — the
  // first distance at which the minimum is reached, not the last, which is 200 on
  // every weapon in the game and would put every chart on the same useless scale.
  const floorAt = Math.max(
    firstAt(pBase, pBase[pBase.length - 1]?.[1]) ?? 0,
    firstAt(pBuilt, pBuilt[pBuilt.length - 1][1]) ?? 0
  );
  const maxD = Math.max(12, Math.min(end, Math.max(30, (floorAt || end) * 1.35)));
  const peak = Math.max(...pBuilt.map((q) => q[1] * pelU), ...pBase.map((q) => q[1] * pelB));
  const maxV = Math.max(1, peak * 1.16);
  const px = (d) => n1(L + (Math.min(d, maxD) / maxD) * (W - L - R));
  const py = (v) => n1(T + (1 - Math.min(v, maxV) / maxV) * (H - T - B));

  const steps = [];
  const count = Math.max(24, Math.min(400, Math.ceil(maxD)));
  let run = null;
  for (let i = 0; i <= count; i++) {
    const d = (i / count) * maxD;
    const n = stk(interp(pBuilt, d) * pelU);
    if (!run || run.n !== n) {
      if (run) run.to = d;
      run = { n, from: d, to: maxD };
      steps.push(run);
    }
  }
  if (run) run.to = maxD;

  const bands = steps
    .map((s, i) => {
      const x0 = px(s.from);
      const x1 = px(s.to);
      const w = Math.max(0, x1 - x0);
      const label =
        w > 26
          ? `<text class="gs-ch-stk" x="${n1((x0 + x1) / 2)}" y="${T - 6}" text-anchor="middle">${s.n} STK</text>`
          : '';
      return `<rect class="gs-ch-band${i % 2 ? ' is-alt' : ''}" x="${x0}" y="${T}" width="${n1(w)}" height="${H - T - B}"></rect>${
        i > 0 ? `<line class="gs-ch-step" x1="${x0}" y1="${T}" x2="${x0}" y2="${H - B}"></line>` : ''
      }${label}`;
    })
    .join('');

  const path = (pts, pel) => {
    const drawn = pts.filter(([d]) => d <= maxD);
    if (!drawn.length || drawn[drawn.length - 1][0] < maxD) drawn.push([maxD, interp(pts, maxD)]);
    return drawn.map(([d, v], i) => `${i ? 'L' : 'M'}${px(d)},${py(v * pel)}`).join(' ');
  };

  const gy = [0, 0.5, 1]
    .map((f) => {
      const v = maxV * f;
      return `<line class="gs-ch-grid" x1="${L}" y1="${py(v)}" x2="${W - R}" y2="${py(v)}"></line>
              <text class="gs-ch-y" x="${L - 5}" y="${py(v) + 3}" text-anchor="end">${Math.round(v)}</text>`;
    })
    .join('');

  const ticks = [0, 0.25, 0.5, 0.75, 1]
    .map((f) => {
      const d = maxD * f;
      const anchor = f === 0 ? 'start' : f === 1 ? 'end' : 'middle';
      return `<text class="gs-ch-x" x="${px(d)}" y="${H - 8}" text-anchor="${anchor}">${Math.round(d)}</text>`;
    })
    .join('');

  return `<figure class="gs-chart">
      <figcaption>DAMAGE OVER DISTANCE<i>DIM = AS ISSUED</i></figcaption>
      <svg viewBox="0 0 ${W} ${H}" role="img" preserveAspectRatio="xMidYMid meet"
           aria-label="Damage over distance, as issued against as built">
        ${bands}${gy}
        <path class="gs-ch-base" d="${path(pBase, pelB)}"></path>
        <path class="gs-ch-line" d="${path(pBuilt, pelU)}"></path>
        ${ticks}
      </svg>
      <div class="gs-chart-x">METRES</div>
    </figure>`;
}

/**
 * The recoil path — where the muzzle actually goes over thirty rounds.
 *
 * The pattern table is a list of *increments*, so the visible path is its running
 * sum, and because the table is deterministic the shape below is the shape every
 * burst walks. That is the only reason this chart is worth drawing: a plotted
 * random cloud would teach a player nothing, and this one is learnable.
 */
function recoilChart(base, built) {
  const pat = base?.pattern;
  if (!Array.isArray(pat) || pat.length < 2) return '';
  const shots = Math.max(2, Math.min(30, num(built.magSize, 30) || 30));

  const walk = (def) => {
    const pitch = num(def?.recoil?.pitch);
    const yaw = num(def?.recoil?.yaw);
    const out = [[0, 0]];
    let x = 0;
    let y = 0;
    for (let i = 0; i < shots; i++) {
      const e = pat[Math.min(i, pat.length - 1)] ?? [0, 0];
      y += (num(e[0]) * pitch * 180) / Math.PI;
      x += (num(e[1]) * yaw * 180) / Math.PI;
      out.push([x, y]);
    }
    return out;
  };
  const a = walk(base);
  const b = walk(built);
  const all = a.concat(b);
  const xs = all.map((p) => p[0]);
  const ys = all.map((p) => p[1]);
  const spanX = Math.max(1.2, Math.max(...xs.map(Math.abs)) * 2.3);
  const maxY = Math.max(1, Math.max(...ys));

  const W = 190;
  const H = 176;
  const T = 18;
  const B = 22;
  const px = (v) => n1(W / 2 + (v / spanX) * (W - 26));
  const py = (v) => n1(H - B - (v / maxY) * (H - T - B));
  const line = (pts) => pts.map(([x, y], i) => `${i ? 'L' : 'M'}${px(x)},${py(y)}`).join(' ');
  const dots = b
    .map(([x, y], i) => (i && i % 5 === 0 ? `<circle class="gs-rc-dot" cx="${px(x)}" cy="${py(y)}" r="1.9"></circle>` : ''))
    .join('');

  return `<figure class="gs-chart is-narrow">
      <figcaption>RECOIL PATH · ${shots} RND<i>DIM = AS ISSUED</i></figcaption>
      <svg viewBox="0 0 ${W} ${H}" role="img" preserveAspectRatio="xMidYMid meet"
           aria-label="Recoil path over a magazine, as issued against as built">
        <line class="gs-ch-grid" x1="${W / 2}" y1="${T}" x2="${W / 2}" y2="${H - B}"></line>
        <line class="gs-ch-grid" x1="8" y1="${py(0)}" x2="${W - 8}" y2="${py(0)}"></line>
        <path class="gs-rc-base" d="${line(a)}"></path>
        <path class="gs-rc-line" d="${line(b)}"></path>
        ${dots}
        <text class="gs-ch-y" x="6" y="${T + 4}" text-anchor="start">${round(maxY, 1)}°</text>
      </svg>
      <div class="gs-chart-x">DEGREES OFF AIM · DOT EVERY 5 RND</div>
    </figure>`;
}

function spreadBlock(mBase, mBuilt) {
  const rows = [
    ['HIP · STILL', 'hipCone', 90],
    ['HIP · MOVING', 'hipMoving', 140],
    ['ADS · MOVING', 'adsMoving', 20],
  ]
    .map(([label, key, scale]) => {
      const a = mBase[key];
      const b = mBuilt[key];
      if (a == null && b == null) return '';
      const w = (v) => Math.max(1.5, Math.min(100, ((v ?? 0) / scale) * 100));
      const moved = !near(a, b);
      return `<div class="gs-cone${moved ? (b < a ? ' is-good' : ' is-bad') : ''}">
          <span>${label}</span>
          <i><b class="gs-cone-base" style="width:${w(a).toFixed(1)}%"></b><b class="gs-cone-built" style="width:${w(
            b
          ).toFixed(1)}%"></b></i>
          <u>${b == null ? '—' : round(b, 2)}<s>mrad</s></u>
        </div>`;
    })
    .join('');
  return `<section class="gs-panelblock">
      <h4>CONE OF FIRE</h4>${rows}
      <div class="gs-note is-tight">Half-angles. 1 mrad is 1 cm of spread at 10 m. ADS standing still is
        exactly zero on every weapon in this game, so it is not drawn.</div>
    </section>`;
}

function ttkBlock(base, built) {
  const dists = [5, 15, 25, 40, 60];
  const pb = normDamage(base);
  const pu = normDamage(built);
  const pelB = num(base.pellets, 1) || 1;
  const pelU = num(built.pellets, 1) || 1;
  const rows = dists
    .map((d) => {
      const nb = stk(interp(pb, d) * pelB);
      const nu = stk(interp(pu, d) * pelU);
      const tb = nb ? ttkFor(nb, base) * 1000 : null;
      const tu = nu ? ttkFor(nu, built) * 1000 : null;
      const moved = !near(tb, tu);
      return `<div class="gs-ttk${moved ? (tu < tb ? ' is-good' : ' is-bad') : ''}">
          <span>${d} m</span>
          <b>${nu || '—'}<i>STK</i></b>
          <u>${tu == null ? '—' : Math.round(tu)}<i>ms</i></u>
          <s>${moved && tb != null ? `WAS ${Math.round(tb)}` : ''}</s>
        </div>`;
    })
    .join('');
  return `<section class="gs-panelblock">
      <h4>TIME TO KILL · ${ENEMY_HP} HP</h4>${rows}
      <div class="gs-note is-tight">Measured from the first round landing, every round on target, and
        gated by the action rather than the fire rate where the weapon is bolt, pump or burst.</div>
    </section>`;
}

/* ------------------------------ number helpers ----------------------------- */

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const round = (v, p = 0) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const k = 10 ** p;
  const r = Math.round(n * k) / k;
  return p > 0 ? r.toFixed(p) : String(r);
};
/**
 * One decimal, still a *number*.
 *
 * `round()` returns a string because it feeds text; using it for SVG geometry makes
 * `round(y) + 3` produce "1323" from 132, which places a label a thousand units
 * off-canvas and is invisible rather than obviously wrong. Coordinates get this.
 */
const n1 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 10) / 10 : 0;
};
const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const mradOf = (v) => (v == null || !Number.isFinite(Number(v)) ? null : Number(v) * 1000);
const degOf = (v) => (v == null || !Number.isFinite(Number(v)) ? null : (Number(v) * 180) / Math.PI);

/** Equal for display purposes. Floating multiplies leave 0.30000000000000004 behind. */
function near(a, b) {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  const x = Number(a);
  const y = Number(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return x === y;
  return Math.abs(x - y) <= Math.max(1e-4, Math.abs(x) * 1e-4);
}

function normDamage(def) {
  const raw = def?.damage;
  if (!Array.isArray(raw)) return [];
  const pts = raw
    .map((p) => (Array.isArray(p) ? [Number(p[0]), Number(p[1])] : null))
    .filter((p) => p && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  pts.sort((a, b) => a[0] - b[0]);
  return pts;
}

function interp(pts, d) {
  if (!pts.length) return 0;
  if (d <= pts[0][0]) return pts[0][1];
  for (let i = 1; i < pts.length; i++) {
    if (d <= pts[i][0]) {
      const [d0, v0] = pts[i - 1];
      const [d1, v1] = pts[i];
      const t = (d - d0) / Math.max(1e-6, d1 - d0);
      return v0 + (v1 - v0) * t;
    }
  }
  return pts[pts.length - 1][1];
}

/** The greatest distance at which damage is still `v`. */
function lastAt(pts, v) {
  let out = pts[0]?.[0] ?? 0;
  for (const [d, dv] of pts) if (Math.abs(dv - v) < 1e-6) out = d;
  return Math.round(out * 10) / 10;
}

/** The first distance at which damage has fallen to `v`. */
function firstAt(pts, v) {
  for (const [d, dv] of pts) if (Math.abs(dv - v) < 1e-6) return Math.round(d * 10) / 10;
  return null;
}

const stk = (dmg) => (dmg > 0 ? Math.max(1, Math.ceil(ENEMY_HP / dmg)) : 0);

/**
 * Time to land `shots`, counting from the first round hitting.
 *
 * The gap between rounds is not always `60 / rpm`: a bolt gun is gated by its bolt
 * cycle and a pump by its pump, and a burst weapon pays a dead delay after every
 * completed burst. This matters more here than on the stat sheet, because the TRIGGER
 * GROUP slot can turn an automatic into a burst weapon and the TTK has to follow.
 */
function ttkFor(shots, def) {
  const rpm = num(def?.rpm);
  if (!shots || shots < 1 || !rpm) return null;
  const n = shots - 1;
  if (n === 0) return 0;
  if (def.fireMode === 'bolt') return n * (num(def.boltCycle) || 60 / rpm);
  if (def.fireMode === 'pump') return n * (num(def.pumpTime) || 60 / rpm);
  const iv = 60 / rpm;
  if (def.fireMode === 'burst' && num(def.burstCount) > 0) {
    return n * iv + Math.floor(n / num(def.burstCount)) * num(def.burstDelay);
  }
  return n * iv;
}

function climbOverMag(def) {
  const pat = def?.pattern;
  const pitch = num(def?.recoil?.pitch);
  if (!Array.isArray(pat) || !pat.length || !pitch) return null;
  const n = Math.max(1, Math.min(200, num(def.magSize, pat.length)));
  let sum = 0;
  for (let i = 0; i < n; i++) sum += Math.abs(num(pat[Math.min(i, pat.length - 1)]?.[0]));
  return (sum * pitch * 180) / Math.PI;
}

function driftOverMag(def) {
  const pat = def?.pattern;
  const yaw = num(def?.recoil?.yaw);
  if (!Array.isArray(pat) || !pat.length || !yaw) return null;
  const n = Math.max(1, Math.min(200, num(def.magSize, pat.length)));
  let sum = 0;
  for (let i = 0; i < n; i++) sum += num(pat[Math.min(i, pat.length - 1)]?.[1]);
  return (Math.abs(sum) * yaw * 180) / Math.PI;
}

/** The next part this weapon unlocks above `level`, or null once they are all yours. */
function nextUnlockFor(catalog, level) {
  let best = null;
  for (const a of catalog ?? []) {
    if (a.unlockLevel <= level) continue;
    if (!best || a.unlockLevel < best.unlockLevel) best = a;
  }
  return best;
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
}

/* ============================================================================ *
 * Catalog normalisation.
 *
 * `WeaponLoadouts.catalogFor` already answers per weapon, in ladder order, with the
 * unlock level and whether it is earned. This only puts it into the shape the list
 * builder reads and drops anything that names a slot this weapon does not have —
 * which cannot happen today, and which would put an unfittable row on the screen if
 * the compatibility table and the ladder ever disagreed.
 * ============================================================================ */

function normaliseCatalog(rows, slots) {
  if (!Array.isArray(rows)) return [];
  const seen = new Set();
  const out = [];
  for (const a of rows) {
    if (!a || typeof a !== 'object' || !a.id) continue;
    if (!slots.includes(a.slot) || seen.has(a.id)) continue;
    seen.add(a.id);
    out.push({
      id: String(a.id),
      slot: String(a.slot),
      name: String(a.name ?? a.id).toUpperCase(),
      blurb: a.blurb ?? '',
      pro: a.pro ?? null,
      con: a.con ?? null,
      cost: Math.max(1, Math.round(num(a.cost, 1))),
      mastery: !!a.mastery,
      unlocked: !!a.unlocked,
      unlockLevel: Math.max(1, Math.min(WEAPON_MAX_LEVEL, Math.round(num(a.unlockLevel, 1)))),
    });
  }
  // Within a slot, in the order they are earned. Across slots the list builder groups
  // them, so this sort only ever decides the order inside one header.
  out.sort((a, b) => a.unlockLevel - b.unlockLevel || a.name.localeCompare(b.name));
  return out;
}
